

<script setup>
import { ref, watch, nextTick, computed } from 'vue';
import FloatingSelect from '@/components/FloatingSelect.vue';
import BusinessPartner from '../../views/SinglePages/BusinessPartner.vue';

const props = defineProps({
  status: { type: Object, required: true },
  campaignActive: { type: Boolean, required: true },
  currentComment: { type: String, default: '' },
  onUpdateComment: { type: Function, required: true },
  contacts: { type: Array, default: () => [] },
  decisionMakerNeeded: { type: Boolean, default: false },
  decisionMakerInvalid: { type: Boolean, default: false },
  selectedContactId: { type: [String, Number], default: '' },
  onUpdateSelectedContactId: { type: Function, required: true },
  isValidEmail: { type: Function, required: true },
  immediateCloseEnabled: { type: Boolean, default: false },
  immediateCloseLoading: { type: Boolean, default: false },
  handleImmediateClose: { type: Function, default: () => {} },
  showNextButton: { type: Boolean, default: true },
  showSaveButton: { type: Boolean, default: true },
  isSaveDisabled: { type: Boolean, default: false },
  handleGoToPreviousStep: { type: Function, required: true },
  handleGoToNextStep: { type: Function, required: true },
  handleSaveSalesPitch: { type: Function, required: true },
  saveSalesPitchIsLoading: { type: Boolean, default: false },
  saveRequirementsMessage: { type: String, default: '' },
  saveBlockingReasons: { type: Array, default: () => [] },
  handleChangeStatus: { type: Function, required: true },
  sourceSugar: { type: String, default: '' },
  gpNo: { type: String, default: '' },
});

const UNKNOWN_DECISION_MAKER_ID = '__unknown__';

// Keep comment stable across re-renders using local state synced with prop
const localComment = ref(props.currentComment || '');
watch(() => props.currentComment, (val) => { localComment.value = val ?? ''; });
watch(localComment, (val) => { props.onUpdateComment(val ?? ''); });

// Ensure latest localComment is flushed before save/close actions
const handleSaveClick = () => {
  try { props.onUpdateComment(localComment.value ?? ''); } catch(e) {}
  try { console.log('POST: [SalesPitchStatus] Save clicked. localComment =', localComment.value); } catch(e) {}
  props.handleSaveSalesPitch(localComment.value);
};
const handleImmediateCloseClick = () => {
  try { props.onUpdateComment(localComment.value ?? ''); } catch(e) {}
  try { console.log('POST: [SalesPitchStatus] Immediate close clicked. localComment =', localComment.value); } catch(e) {}
  props.handleImmediateClose(localComment.value);
};

// Emoji picker state and helpers
const noteRef = ref(null);
const showEmojiPicker = ref(false);
const emojis = [
  '😀','😊','😍','😉','👍','🙏','🎉','✅','❌','🔥',
  '💡','📞','📧','✍️','📅','🕒','📌','🚀','😅','🤔',
  '😎','🤝','🫶','💬','📈','📉','📝','📎','📨','📲',
  '⭐','🌟','⚠️','ℹ️','🔁','🔄','💤'
];
function toggleEmoji() {
  showEmojiPicker.value = !showEmojiPicker.value;
}
function insertEmoji(emoji) {
  const el = noteRef.value;
  const current = localComment.value || '';
  if (el && typeof el.selectionStart === 'number' && typeof el.selectionEnd === 'number') {
    const start = el.selectionStart;
    const end = el.selectionEnd;
    localComment.value = current.slice(0, start) + emoji + current.slice(end);
    nextTick(() => {
      try { el.focus(); el.selectionStart = el.selectionEnd = start + emoji.length; } catch(e) {}
    });
  } else {
    localComment.value = (current + emoji);
  }
  showEmojiPicker.value = false;
}

// --- Decision Maker dropdown options with Neu/Haupt pills ---
function getEmail(c) {
  return (c?.email || (Array.isArray(c?.emailAddresses) ? c.emailAddresses[0] : undefined) || (Array.isArray(c?.emailList) ? c.emailList[0]?.name : undefined) || '').trim();
}
function getSugarId(c) {
  const direct = c?.idSugar;
  const nested = c?.sugar?.id;
  const value = (direct ?? nested ?? '').toString().trim();
  return value.length ? value : null;
}
const newestContactId = computed(() => {
  const list = Array.isArray(props.contacts) ? props.contacts : [];
  let latest = null;
  for (const c of list) {
    if (!c) continue;
    const dc = c.dateCreated ? new Date(c.dateCreated) : null;
    if (!latest) { latest = c && dc ? c : latest; continue; }
    const dl = latest?.dateCreated ? new Date(latest.dateCreated) : null;
    if (dc && (!dl || dc > dl)) latest = c;
  }
  return latest?.id ?? null;
});
const dmOptions = computed(() => {
  const list = Array.isArray(props.contacts) ? props.contacts : [];
  return list.map(c => {
    // Email: aus email, emailAddresses oder emailList
    const email = c.email
      || (Array.isArray(c.emailAddresses) && c.emailAddresses.length > 0 ? c.emailAddresses[0] : undefined)
      || (Array.isArray(c.emailList) && c.emailList.length > 0 ? c.emailList[0].name : undefined)
      || '';
    const emailValid = !!(email && props.isValidEmail(email));
    // Anrede: gender ("m"/"w"/"d") oder salutation oder salutationInformal
    const salutationValid = !!(c.salutation && String(c.salutation).trim().length > 0)
      || !!(c.gender && String(c.gender).trim().length > 0)
      || !!c.salutationInformal;
    const firstNameValid = !!(c.firstName && String(c.firstName).trim().length > 0);
    const lastNameValid = !!(c.lastName && String(c.lastName).trim().length > 0);
    const isComplete = salutationValid && firstNameValid && lastNameValid && emailValid;
    const disabled = !isComplete;
    return {
      id: c.id,
      label: `${(c.lastName || '-unbekannt-')}${c.firstName ? (', ' + c.firstName) : ''} - ${email || 'Keine gültige E-Mail'}`,
      firstName: c.firstName,
      lastName: c.lastName,
      salutation: c.salutation,
      gender: c.gender,
      salutationInformal: c.salutationInformal,
      email,
      main: !!c.main,
      isNewest: c.id === newestContactId.value,
      idSugar: getSugarId(c),
      disabled,
      isIncomplete: !isComplete
    };
  });
});

// Determine what was selected on step 1 regarding whether a decision maker exists
const decisionOnStep1 = computed(() => {
  const sec = props.status && props.status['Entscheider Kontakt hergestellt'];
  if (!sec || !sec.options) return null;
  for (const [name, opt] of Object.entries(sec.options)) {
    if (opt && opt.checked) {
      const n = String(name || '').toLowerCase();
      if (n.includes('ja')) return 'yes';
      if (n.includes('nein')) return 'no';
      // fallback: explicit truthy value on option could mean yes
      return 'yes';
    }
  }
  return null;
});

// Decision maker options with special top entry 'ohne Angabe'.
// The disabled state of the special option is derived from `decisionOnStep1` so
// it is reactive and consistent without mutating computed output later.
const decisionMakerOptions = computed(() => {
  const base = Array.isArray(dmOptions.value) ? [...dmOptions.value] : [];
  const unknownOption = {
    id: UNKNOWN_DECISION_MAKER_ID,
    label: 'ohne Angabe',
    firstName: '',
    lastName: '',
    salutation: '',
    gender: '',
    salutationInformal: false,
    email: '',
    main: false,
    isNewest: false,
    idSugar: null,
    // disabled when step1 explicitly said there's a decision maker
    disabled: decisionOnStep1.value === 'yes',
    isIncomplete: false,
    isUnknown: true,
  };
  return [unknownOption, ...base];
});

// If the user indicated on step1 that there's no decision maker, auto-select 'ohne Angabe' on step 2
watch([
  () => props.status.currentStep,
  decisionOnStep1,
  () => decisionMakerOptions.value
], ([step, dec]) => {
  if (step === 2 && dec === 'no') {
    // Only set if not already set to unknown
    if (String(props.selectedContactId) !== String(UNKNOWN_DECISION_MAKER_ID)) {
      updateSelectedContactId(UNKNOWN_DECISION_MAKER_ID);
    }
  }
}, { immediate: true });


const showSaveButtonVisible = computed(() => {
  if (!props.showSaveButton) return false;
  if (props.status.currentStep === 2 && isVerbindlicherTerminSelected.value) {
    return !!(selectedDmOption.value && !selectedDmOption.value.disabled);
  }
  return true;
});

// Zentrale Validierung: Niemals unvollständigen Entscheider selektieren
function updateSelectedContactId(newId) {
  if (newId === '' || newId === null || newId === undefined) {
    props.onUpdateSelectedContactId('');
    return;
  }
  const found = decisionMakerOptions.value.find(o => String(o.id) === String(newId));
  if (!found || found.disabled) {
    props.onUpdateSelectedContactId('');
    return;
  }
  props.onUpdateSelectedContactId(found.id);
}

// Watcher: Fallback, falls von außen ein ungültiger Wert gesetzt wird
watch([
  () => props.selectedContactId,
  () => decisionMakerOptions.value
], ([selectedId, options]) => {
  if (selectedId === '' || selectedId === null || selectedId === undefined) return;
  const found = options.find(o => String(o.id) === String(selectedId));
  if (found && found.disabled) {
    updateSelectedContactId('');
  }
});

// Normalize selected id for matching regardless of type (string/number)
const normalizedSelectedId = computed(() => {
  const v = props.selectedContactId;
  if (v === '' || v === null || v === undefined) return null;
  const num = Number(v);
  return Number.isNaN(num) ? String(v) : num;
});

// Currently selected option object for trigger preview
const selectedDmOption = computed(() => {
  const id = normalizedSelectedId.value;
  if (id === null) return null;
  return decisionMakerOptions.value.find(o => String(o.id) === String(id)) || null;
});

const isVerbindlicherTerminSelected = computed(() => {
  const section = props.status?.['Verbindlicher Termin mit Entscheider'];
  const options = section?.options;
  if (!options || typeof options !== 'object') return false;
  return Object.values(options).some(opt => opt?.checked === true);
});

// Beim Bearbeiten eines bestehenden Entscheiders sind Vorname/Nachname/E-Mail gesperrt (siehe DecisionMaker.vue),
// nachträglich ergänzbar ist praktisch nur eine fehlende Anrede. Der Hinweistext muss das widerspiegeln,
// statt pauschal "bearbeiten" vorzuschlagen, wenn das in den meisten Fällen gar nicht möglich ist.
function isSalutationOnlyIssue(option) {
  const hasSalutation = !!(option.salutation && String(option.salutation).trim())
    || !!(option.gender && String(option.gender).trim())
    || !!option.salutationInformal;
  const hasFirstName = !!(option.firstName && String(option.firstName).trim());
  const hasLastName = !!(option.lastName && String(option.lastName).trim());
  const hasEmail = !!(option.email && props.isValidEmail(option.email));
  return !hasSalutation && hasFirstName && hasLastName && hasEmail;
}

// Nutzt dieselbe Grundlage wie showSaveButtonVisible (Vollständigkeits-Check inkl. Anrede/Name),
// statt props.decisionMakerNeeded, das nach "Ja" in Schritt 1 kurzzeitig noch true ist, bevor
// in Schritt 2 überhaupt eine Option gewählt wurde (Meldung würde sonst zu früh erscheinen).
const decisionMakerBlockMessage = computed(() => {
  if (props.status.currentStep !== 2 || !isVerbindlicherTerminSelected.value) return '';
  if (selectedDmOption.value && !selectedDmOption.value.disabled) return '';
  if (!dmOptions.value.length) {
    return 'Es ist kein Entscheider vorhanden. Bitte lege einen neuen gültigen Entscheider an, um weiterarbeiten zu können.';
  }
  const disabledOptions = dmOptions.value.filter(option => option.disabled);
  if (disabledOptions.length === dmOptions.value.length) {
    const single = disabledOptions.length === 1;
    const allFixableByEdit = disabledOptions.every(isSalutationOnlyIssue);
    const noneFixableByEdit = disabledOptions.every(option => !isSalutationOnlyIssue(option));
    if (allFixableByEdit) {
      return single
        ? 'Beim vorhandenen Entscheider fehlt nur die Anrede (Herr/Frau). Bitte ergänze sie über "Bearbeiten", um weiterarbeiten zu können.'
        : 'Bei den vorhandenen Entscheidern fehlt jeweils nur die Anrede (Herr/Frau). Bitte ergänze sie über "Bearbeiten", um weiterarbeiten zu können.';
    }
    if (noneFixableByEdit) {
      return single
        ? 'Dem vorhandenen Entscheider fehlen Vorname, Nachname oder E-Mail – das lässt sich nachträglich nicht ergänzen. Bitte lege einen neuen, vollständigen Entscheider an, um weiterarbeiten zu können.'
        : 'Den vorhandenen Entscheidern fehlen jeweils Vorname, Nachname oder E-Mail – das lässt sich nachträglich nicht ergänzen. Bitte lege einen neuen, vollständigen Entscheider an, um weiterarbeiten zu können.';
    }
    return 'Bei manchen Entscheidern fehlt nur die Anrede (über "Bearbeiten" ergänzbar), bei anderen fehlen Vorname, Nachname oder E-Mail – dafür ist ein neuer, vollständiger Entscheider nötig, um weiterarbeiten zu können.';
  }
  return 'Bitte wähle einen vollständigen Entscheider mit gültiger E-Mail-Adresse aus, um weiterarbeiten zu können.';
});

// Immer den aktuellen Haupt-Entscheider (main:true) auswählen, wenn sich die Kontakte ändern
watch(
  [() => props.contacts, () => dmOptions.value],
  ([list, options]) => {
    // Bereits ausgewählten (gültigen) Wert beibehalten, inkl. "Entscheider nicht bekannt"
    if (selectedDmOption.value && !selectedDmOption.value.disabled) {
      return;
    }
    const mainOption = options.find(o => o.main && !o.disabled);
    if (mainOption) {
      updateSelectedContactId(mainOption.id);
      return;
    }
    // Fallback: Nur ein gültiger Kontakt
    if (options.length === 1 && !options[0].disabled) {
      updateSelectedContactId(options[0].id);
      return;
    }
    // Fallback: Neuester Kontakt
    const newestOption = options.find(o => o.isNewest && !o.disabled);
    if (newestOption) {
      updateSelectedContactId(newestOption.id);
      return;
    }
  },
  { immediate: true }
);
</script>

<template>
  <div :class="['container accordion-menu editing']">
    <h4>Neuer Kontaktierungsstatus</h4>
    <div class="multiform" v-if="campaignActive">
      <span class="stepIndex">Schritt {{ status.currentStep }}:</span>
      <div class="multiform-step" v-if="status.currentStep === 1">
        <div class="container form-step" id="_2" v-if="status['Entscheider Kontakt hergestellt'].display===true">
          <h5>Entscheider Kontakt</h5>
          <div class="input-wrapper" v-for="(opt, name, index) in status['Entscheider Kontakt hergestellt'].options" :key="index">
            <input type="radio" name="contact" :id="name" @click="handleChangeStatus('Entscheider Kontakt hergestellt', name)">
            <label :for="name">{{ name }}</label>
          </div>
        </div>
      </div>
      <div class="multiform-step" v-if="status.currentStep === 2">
        <div class="container form-step" id="_22" v-if="status['Beendet'].display===true">
          <h5>Beendet</h5>
          <div v-for="(opt, name) in status['Beendet'].options" :key="name">
            <div v-if="opt.display===true" class="input-wrapper" >
              <input type="radio" name="next_contact" :id="name"  @click="handleChangeStatus('Beendet', name)">
              <label :for="name">{{ name }}</label>
            </div>
          </div>
        </div>
        <div class="container form-step" id="_2" v-if="status['Unverbindliche Wiedervorlage'].display === true">
          <!-- <h5>Unverbindliche Wiedervorlage für</h5> -->
          <h5>Unverbindliche Wiedervorlage</h5>
          <div v-for="(opt, name) in status['Unverbindliche Wiedervorlage'].options" :key="name">
            <div v-if="opt.display === true" class="input-wrapper">
              <input type="radio" name="next_contact" :id="name" @click="handleChangeStatus('Unverbindliche Wiedervorlage', name)">
              <label :for="name">{{ name }}</label>
            </div>
          </div>
          <div class="datePicker">
            <div class="input-wrapper">
              <input type="date" v-model="status.datePicker['unverbindlich'].selected.date" @change="handleChangeStatus('Auswahl Datum')" :min="status.datePicker['unverbindlich'].selected.dateMin" :max="status.datePicker['unverbindlich'].selected.dateMax" />
              <input type="time" v-model="status.datePicker['unverbindlich'].selected.time" @change="handleChangeStatus('Auswahl Zeit')" />
              {{ status.datePicker.addendum }}
            </div>
          </div>
        </div>
        <div class="container form-step" id="_6" v-if="status['Verbindlicher Termin mit Entscheider'].display===true">
          <!-- <h5>Verbindlicher Termin mit Entscheider</h5> -->
          <h5>Verbindlicher Termin mit Entscheider</h5>
          <div v-for="(opt, name) in status['Verbindlicher Termin mit Entscheider'].options" :key="name">
            <div v-if="opt.display===true" class="input-wrapper" >
              <input type="radio" name="next_contact" :id="name" @click="handleChangeStatus('Verbindlicher Termin mit Entscheider', name)">
              <label :for="name">{{ name }}</label>
            </div>
          </div>
          <div class="datePicker">
            <div class="input-wrapper">
              <input type="date" v-model="status.datePicker['verbindlich'].selected.date" @change="handleChangeStatus('Auswahl Datum')" :min="status.datePicker['verbindlich'].selected.dateMin" :max="status.datePicker['verbindlich'].selected.dateMax" />
              <input type="time" v-model="status.datePicker['verbindlich'].selected.time" @change="handleChangeStatus('Auswahl Zeit')" />
              {{ status.datePicker.addendum }}
            </div>
          </div>
        </div>
      </div>
      <div class="note-wrapper">
        <textarea ref="noteRef" class="note" v-model="localComment" placeholder="Notiz (Optional)"></textarea>
        <button type="button" class="emoji-btn" title="Emoji einfügen" @click.stop="toggleEmoji">😊</button>
        <div v-if="showEmojiPicker" class="emoji-panel" @click.stop>
          <button v-for="(e,i) in emojis" :key="i" class="emoji-item" @click="insertEmoji(e)">{{ e }}</button>
        </div>
      </div>
      <div class="decision-maker-select" v-if="status.currentStep === 2 && decisionMakerOptions.length > 0">
        <label class="dm-label">Entscheider E-Mail</label>
        <FloatingSelect size="sm" :options="decisionMakerOptions" optionKey="id" :optionLabel="o=>o.label" :return-object="false" :match-trigger-width="true"
          :disabled="decisionMakerOptions.length < 1" aria-label="Entscheider wählen" :model-value="selectedContactId"
          :search-prop="['firstName','lastName','email','label']"
          :optionDisabled="o => o.disabled"
          @select="val => updateSelectedContactId(val && val.id !== undefined ? val.id : val)"
          @update:modelValue="val => updateSelectedContactId(val && val.id !== undefined ? val.id : val)">
          <template #trigger="{ open }">
            <button type="button" class="btn btn--pill btn--sm btn--outline dm-trigger" :aria-expanded="open.toString()">
              <template v-if="selectedDmOption && !selectedDmOption.disabled">
                <span class="cur-label">{{ selectedDmOption.label }}</span>
                <span class="dm-badges">
                  <span v-if="selectedDmOption.isNewest" class="dm-pill dm-new">Neu</span>
                  <span v-if="selectedDmOption.main" class="dm-pill dm-main">Haupt</span>
                </span>
              </template>
              <span class="cur-label" v-else>Bitte Entscheider auswählen</span>
              <svg class="chevron" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path fill="currentColor" d="M7 10l5 5 5-5z"/></svg>
            </button>
          </template>
          <template #item="{ option, highlighted }">
            <div class="dm-item" :class="{ 'is-highlighted': highlighted, 'is-disabled': option.disabled }">
              <div class="dm-meta">
                <span class="dm-name">{{ option.label }}</span>
                <span v-if="option.isNewest" class="dm-pill dm-new">Neu</span>
                <span v-if="option.main" class="dm-pill dm-main">Haupt</span>
                <span v-if="option.isIncomplete" class="dm-pill dm-unvollstaendig">Unvollständig</span>
              </div>
            </div>
          </template>
        </FloatingSelect>
      </div>
      <div v-if="decisionMakerBlockMessage" class="decision-maker-blocker" role="alert">
        {{ decisionMakerBlockMessage }}
      </div>
      
      <div
        v-if="status.currentStep === 2 && isVerbindlicherTerminSelected && selectedDmOption && !selectedDmOption.idSugar"
        class="lead-warning"
      >
        <span class="warning-icon" aria-hidden="true">&#9888;</span>
        <span class="warning-text">
          <strong>Vorsicht:</strong> Bei Entscheidern ohne SugarID können aus technischen Gründen keine Terminbestätigungen an gewählte Entscheider versendet werden. </span>
      </div>
      <div class="multiform-navigation nav-layout">
        <div class="nav-left">
          <button v-if="status.currentStep>1" @click="handleGoToPreviousStep()">Zurück</button>
          <button
            v-if="false"
            class="immediate-close"
            :disabled="!immediateCloseEnabled || immediateCloseLoading"
            :class="{'disabled': !immediateCloseEnabled, 'loading': immediateCloseLoading}"
            @click="handleImmediateCloseClick"
          >
            <span v-if="!immediateCloseLoading">Sofort Abschluss</span>
            <span v-else class="loader mini"></span>
          </button>
        </div>
        <div class="nav-right">
          <button v-if="showNextButton" @click="handleGoToNextStep()" :disabled="!status.allowNext">Weiter</button>
          <button v-if="showSaveButtonVisible" :class="['save', {'disabled cell-grid': isSaveDisabled}]" :disabled="isSaveDisabled" @click="handleSaveClick">
            Speichern
            <div v-if="saveSalesPitchIsLoading" class="loader"></div>
          </button>
        </div>
      </div>
      
      <ul v-if="isSaveDisabled && saveBlockingReasons.length" class="save-blocking-reasons">
        <li v-for="(r,i) in saveBlockingReasons" :key="i">{{ r }}</li>
      </ul>
    </div>
    <div id="warning" class="container negative" v-else>Keine Bearbeitung möglich – Kampagne ist abgelaufen.</div>
  </div>
</template>

<style scoped>
/* Unbrauchbar-Label für ausgegraute Entscheider */
.note-wrapper { position: relative; }
.note-wrapper .note { width: 100%; padding-right: 2.2rem; }
.emoji-btn {
  position: absolute; right: 8px; bottom: 8px; z-index: 2;
  border: 1px solid rgba(148,163,184,.35); border-radius: 8px;
  background: rgba(0,0,0,.25); color: #fff; width: 32px; height: 32px;
  display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
}
.theme--light .emoji-btn { background: rgba(255,255,255,.9); color: #111827; border-color: rgba(0,0,0,.1); }
.emoji-btn:hover { filter: brightness(1.05); }
.emoji-panel {
  position: absolute; right: 8px; bottom: 44px; z-index: 5;
  background: linear-gradient(135deg,#1f2731,#232e3a); color:#e5e7eb;
  border: 1px solid #334155; border-radius: 10px; padding: .35rem; box-shadow: 0 10px 24px -12px rgba(0,0,0,.6);
  width: 220px; max-height: 220px; overflow: auto; display: grid; grid-template-columns: repeat(8, 1fr); gap: .25rem;
}
.theme--light .emoji-panel { background: #fff; color: #0f172a; border-color: #d5dde5; }
.emoji-item {
  all: unset; cursor: pointer; width: 24px; height: 24px; line-height: 24px; text-align: center; border-radius: 6px;
}
.emoji-item:hover { background: rgba(255,255,255,.12); }
.theme--light .emoji-item:hover { background: rgba(0,0,0,.06); }

/* Decision Maker custom dropdown styles */
.dm-trigger { display:flex; align-items:center; gap:.4rem; padding:.45rem .7rem; width:100%; justify-content:space-between; }
.dm-trigger .chevron { color:#94a3b8; }
.dm-item { display:flex; align-items:center; justify-content:space-between; gap:.5rem; width:100%; }
.dm-item .dm-meta { display:flex; align-items:center; gap:.45rem; width:100%; }
.dm-name { font-weight:600; letter-spacing:.35px; font-size:.66rem; flex:1 1 auto; }
.dm-badges { display:inline-flex; align-items:center; gap:.35rem; margin-left:.35rem; }
.dm-pill { font-size:.6rem; font-weight:800; padding:.14rem .42rem; border-radius:999px; border:1px solid rgba(245,158,11,.45); background:linear-gradient(135deg,#f59e0b,#d97706); color:#fff; }
.theme--light .dm-pill { background:linear-gradient(135deg,#fed7aa,#fdba74); color:#1e293b; border-color:#fdba74; }
.dm-pill.dm-unvollstaendig {
  background: linear-gradient(135deg,#ff5d47,#d91f06);
  color: #fff;
}
.dm-pill.dm-new { text-transform:uppercase; letter-spacing:.3px; }
.dm-pill.dm-main { letter-spacing:.3px; }

.decision-maker-blocker {
  margin: .6rem 0 .4rem;
  padding: .7rem .85rem;
  border: 1.5px solid #fb923c;
  border-radius: 8px;
  background: linear-gradient(90deg, #fff7ed 0%, #ffedd5 100%);
  color: #9a3412;
  font-size: .88rem;
  font-weight: 700;
  line-height: 1.35;
}

/* Moderner Warnhinweis für Leads (Terminbestätigung) */
.lead-warning {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  background: linear-gradient(90deg, #fff7ed 0%, #ffedd5 100%);
  color: #c2410c;
  border: 1.5px solid #fb923c;
  border-radius: 8px;
  padding: 0.55rem 1.2rem 0.55rem 1rem;
  font-size: 1rem;
  font-weight: 500;
  margin: 0.7rem 0 0.7rem 0.2rem;
  box-shadow: 0 2px 8px -4px #fb923c33;
  max-width: 520px;
  min-width: 180px;
  z-index: 10;
  animation: fadeInWarn 0.5s;
}
.lead-warning .warning-icon {
  font-size: 1.35rem;
  color: #ea580c;
  flex-shrink: 0;
  filter: drop-shadow(0 1px 2px #fb923c66);
}
.lead-warning .warning-text {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}
.lead-warning strong {
  font-weight: 700;
  letter-spacing: 0.02em;
}
@keyframes fadeInWarn {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>